import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { SimulationNotification, SimulationNotificationUpdate } from '../models/SimulationNotification';
import { Observable, timer, interval, BehaviorSubject, Subject, EMPTY } from 'rxjs';
import { switchMap, delay, distinctUntilChanged, take, filter, merge, first  } from 'rxjs/operators';
import { User } from '../models/User';
import { Store } from '../../store';
import { isEqual, isNil } from 'lodash-es';



/**
 * Sequence of variable delay values between timers.
 */
const notificationPollingTimeouts = [ 1, 2, 4, 6, 8, 10, 15, 20, 25, 30 ];



/**
 * "Smart" polling for simulation notifications.
 *
 * @export
 * @class NotifPollingService
 */
@Injectable({
  providedIn: 'root'
})
export class NotifPollingService {

  /**
   * Identifier of the user
   *
   * @private
   * @type {string}
   * @memberof NotifPollingService
   */
  private userID: string = null;

  /**
   * Rxjs Subject needed when signaling that the pannel must be displayed.
   *
   * @private
   * @memberof NotifPollingService
   */
  private showNotifPanelSubject = new Subject<void>();

  /**
   * Observable that sends a void value when we need to show the notification panel.
   *
   * @memberof NotifPollingService
   */
  public showNotifPanel$ = this.showNotifPanelSubject.asObservable();

  /**
   * Keep a copy of the latest data sent by the backend.
   *
   * @private
   * @type {SimulationNotification[]}
   * @memberof NotifPollingService
   */
  private storedNotifs: SimulationNotification[] = [];

  /**
   * Access the stored notifications by ID.
   *
   * @private
   * @type {*}
   * @memberof NotifPollingService
   */
  private cachedNotifs: any = {};

  /**
   * This is a periodic timer where the period increases at each step (cf 'timeouts' sequence).
   *
   * @memberof NotifPollingService
   */
  private dynamicTimer$ = this.setupPollingTimers();

  /**
   * Rxjs Subject needed for the polling hot observable.
   *
   * @private
   * @memberof NotifPollingService
   */
  private pollingTrigger$ = new BehaviorSubject(0);

  /**
   * Rxjs subject needed for broadcasting notification updates.
   *
   * @private
   * @memberof NotifPollingService
   */
  private publishedNotifs = new BehaviorSubject<SimulationNotification[]>([]);

  /**
   * This is an observable of the latest version of the notification list.
   *
   * @memberof NotifPollingService
   */
  public publishedNotifs$ = this.publishedNotifs.asObservable();

  /**
   * Hot observable that yields the notifications sent by the backend.
   *
   * @memberof NotifPollingService
   */
  private notifsFromBackend$ = this.pollingTrigger$.pipe(
    /* Generate the sequence of timeout values each time notifPollingTrigger is called. */
    switchMap((x: number) => this.dynamicTimer$),
    /* Ignore timers until the user ID is set. */
    filter((v: number) => !isNil(this.userID)),
    /* Run a fetch request on each timer. */
    switchMap((v: number) => this.dataService.getNotifications(this.userID)),
    /* Do not bother updating the cache if backend data are the same. */
    distinctUntilChanged((prev: SimulationNotification[], curr: SimulationNotification[]) => isEqual(prev, curr)));


  /**
   * Creates an instance of NotifPollingService.
   *
   * Start polling notifications from the backend.
   *
   * @param {DataService} dataService
   * @memberof NotifPollingService
   */
  constructor(private dataService: DataService, private store: Store) {
    const user$ = this.store.select<User>('user').pipe(
      filter((user: User) => (!isNil(user)) && (!isNil(user.queryConfig)) && (!isNil(user.queryConfig) && !isNil(user.uid)))
    );
    user$.subscribe((user: User) => this.userID = user.uid);
    this.notifsFromBackend$.subscribe(
      (items: SimulationNotification[]) => {
        if (this.updateCache(items)) {
          this.broadcastNotificationList();
        }
      }
    );
  }

  /**
   * Create a sequence of timers where the period increases over time.
   *
   * @private
   * @returns {Observable<any>}
   * @memberof NotifPollingService
   */
  private setupPollingTimers(): Observable<any> {
    const timeouts: number[] = notificationPollingTimeouts;
    const nTimeouts: number = notificationPollingTimeouts.length;
    let sum = 0;
    let dynamicTimer$: Observable<number> = EMPTY;
    for (let i = 0 ; i < nTimeouts - 1 ; i += 1) {
      const timer$: Observable<number> = timer(1000 * timeouts[i]).pipe(delay(1000 * sum));
      dynamicTimer$ = dynamicTimer$.pipe(merge(timer$));
      sum += timeouts[i];
    }
    const periodicTimer$: Observable<number> = interval(1000 * timeouts[nTimeouts - 1]).pipe(delay(1000 * sum));
    dynamicTimer$ = dynamicTimer$.pipe(merge(periodicTimer$));
    return dynamicTimer$;
  }

  /**
   * Restart a sequence of notification polling:
   *
   *  - fetch the notifications often at the begining.
   *  - fetch less often as time passes.
   *  - check notifications at least once every 30s
   *
   * @memberof NotifPollingService
   */
  public restartNotificationPolling() {
    this.pollingTrigger$.next(0);
  }

  /**
   * Emit the updated list of notifications.
   *
   * @memberof NotifPollingService
   */
  public broadcastNotificationList(): void {
    this.publishedNotifs.next(this.storedNotifs);
  }

  /**
   * Update a notification items in the cache and send it to the backend.
   *
   * @param {SimulationNotification[]} items
   * @param {any} [callback=(item: SimulationNotification) => {}]
   * @memberof NotifPollingService
   */
  public updateExistingNotifications(items: SimulationNotification[], callback = (item: SimulationNotification) => {}) {
    items.map((item: SimulationNotification) => Object.assign(this.cachedNotifs[item.notifRowKey], item));
    const list: SimulationNotificationUpdate = { notifications: items}
    this.dataService.updateNotifications(list)
    .pipe(first())
    .subscribe((lst: SimulationNotificationUpdate) => lst.notifications.map(obj => callback(obj)));
  }

  /**
   * It may take some time for the backend to update the state of the notifications.
   *
   * => When the backend sends notifications, we may need to update the "isNew" and "isVisible" flags.
   *
   * @private
   * @param {SimulationNotification[]} items
   * @returns {boolean} true if the cached notifications have changed.
   * @memberof NotifPollingService
   */
  private updateCache(items: SimulationNotification[]): boolean {
    let ret = false;
    const newCache: any = {};
    const dirty: SimulationNotification[] = [];
    for (const item of items) {
      const cached: SimulationNotification = this.cachedNotifs[item.notifRowKey];
      /* Completely new notification entry. */
      if (cached === undefined) {
        /* ignore new deleted notifs */
        if (item.status !== 'Deleted') {
          newCache[item.notifRowKey] = item;
          ret = true;
        }
      } else {
        /* Notification status: in-browser cache has higher priority than backend, EXCEPT FOR DELETED NOTIFS. */ 
        if (item.status === 'Deleted') {
          delete (this.cachedNotifs[item.notifRowKey]);
          ret = true;
          continue;
        }
        /* The state of the notification is not updated in the backend yet. */
        if ((cached.isNew !== item.isNew) || (cached.isVisible !== item.isVisible)) {
          item.isNew = cached.isNew;
          item.isVisible = cached.isVisible;
          dirty.push(item);
          ret = true;
        }
        newCache[item.notifRowKey] = item;
      }
    }
    if (dirty.length > 0) {
      this.dataService.updateNotifications({ notifications: dirty }).pipe(take(1)).subscribe();
    }
    /* Some notifications may be deleted in the backend but not in the UI ? (weird edge case) */
    if (!ret) {
      ret = (items.length === this.storedNotifs.length);
    }
    this.cachedNotifs = newCache;
    this.storedNotifs = items.filter(item => item.status !== 'Deleted');
    return ret;
  }

  /**
   * make the notification panel visible.
   *
   * @memberof NotifPollingService
   */
  showNotifPanel(): void {
    this.showNotifPanelSubject.next(undefined);
  }

}
